import { FC, useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Table from "../../table"
import BrandCell from "./cells/brand"
import LeadsCell from "./cells/leads"
import OwnerCell from "./cells/owner"
import MenuCell from "./cells/menu"
import PushToEngagements from "../push-to-engagements"
import {
  useFetchLeadLogsQuery,
  useUpdateSubscriptionMutation
} from "../../../../redux/queries"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  convertSubscriptionDetailsToPayload,
  dateFilterToTimestamps
} from "../../../utils"
import {
  BBColumnDef,
  SubscriptionDetails,
  LeadLog,
  EngagementsTab
} from "../../../../types"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../../activity/date-filter"
import NoResultsInRangeMessage from "../../activity/no-results-in-range-message"

interface Props {
  loid: string | undefined
  orgId?: string
  isSuperAdmin?: boolean
}

const LeadsTable: FC<Props> = ({ loid, orgId, isSuperAdmin }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [updateSubscription] = useUpdateSubscriptionMutation()

  const [selectedLeadLog, setSelectedLeadLog] = useState<LeadLog>()
  const dateFilterRef = useRef<DateFilterHandle>(null)

  const handleClick = useCallback(
    (id: string) => navigate(`/leads/${id}`),
    [navigate]
  )

  const handleEnrollToRateAlerts = useCallback(
    (subscription?: SubscriptionDetails) => {
      if (!subscription) return

      updateSubscription({
        ...convertSubscriptionDetailsToPayload(subscription),
        id: subscription.id,
        active: subscription.active === "1" ? "0" : "1"
      })
    },
    [updateSubscription]
  )

  const handleCreateEngagement = (leadLog: LeadLog) => {
    setSelectedLeadLog(leadLog)
  }

  const cancelCreateEngagement = () => setSelectedLeadLog(undefined)

  const columns: BBColumnDef<LeadLog>[] = useMemo(
    () => [
      {
        id: "leads",
        header: "Leads",
        accessorFn: row => `${row.name} ${row.email}`,
        footer: props => props.column.id,
        cell: props => (
          <LeadsCell
            info={props.row.original}
            onClick={() => handleClick(props.row.original.id)}
          />
        )
      },
      {
        id: "brand",
        header: "Brand",
        accessorFn: row => row.brand_name,
        footer: props => props.column.id,
        cell: props => (
          <BrandCell
            info={props.row.original}
            onClick={() => handleClick(props.row.original.id)}
          />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.owner_email,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell
            info={props.row.original}
            onClick={() => handleClick(props.row.original.id)}
          />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "timestamp",
        header: "",
        accessorFn: row => row.timestamp,
        footer: props => props.column.id,
        cell: props => null,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => (
          <MenuCell
            info={props.row.original}
            onPushToEngagement={handleCreateEngagement}
            onEnrollToRateAlerts={handleEnrollToRateAlerts}
          />
        )
      }
    ],
    [handleClick, handleEnrollToRateAlerts]
  )

  const tabKey = EngagementsTab.leads
  const dateFilter = useAppSelector(state => selectTabRange(state, tabKey))

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, customStartDate?: Date, customEndDate?: Date) => {
      dispatch(
        updateRange({
          tabKey,
          range,
          customStartDate: customStartDate || null,
          customEndDate: customEndDate || null
        })
      )
    },
    [dispatch, tabKey]
  )

  const handleClearDateFilter = useCallback(() => {
    dispatch(
      updateRange({
        tabKey,
        range: "all",
        customStartDate: null,
        customEndDate: null
      })
    )
  }, [dispatch, tabKey])

  const handleViewLast12Months = useCallback(() => {
    const now = new Date()
    const lastYear = new Date(now)
    lastYear.setDate(now.getDate() - 365)
    lastYear.setHours(0, 0, 0, 0)
    dispatch(
      updateRange({
        tabKey,
        range: "custom",
        customStartDate: lastYear,
        customEndDate: now
      })
    )
  }, [dispatch, tabKey])

  // Calculate timestamps from date range
  const { min: timestamp_min, max: timestamp_max } = useMemo(
    () => dateFilterToTimestamps(dateFilter),
    [dateFilter]
  )

  // lead logs
  const { data: leadLogs, isFetching } = useFetchLeadLogsQuery({
    limit: 99999,
    offset: 0,
    ...(timestamp_min !== undefined && timestamp_max !== undefined
      ? { min_created: timestamp_min, max_created: timestamp_max }
      : {}),
    owner: loid,
    org_id: orgId,
    isSuperAdmin
  })

  // All-time existence check (limit 1) — only when current range is empty and not "all"
  const isRangeEmpty = !isFetching && (leadLogs?.length ?? 0) === 0
  const isNotAllTimeRange = dateFilter.range !== "all"
  const { data: anyHistoricalLeads = [], isFetching: isFetchingAllTime } =
    useFetchLeadLogsQuery(
      {
        limit: 1,
        offset: 0,
        owner: loid,
        org_id: orgId,
        isSuperAdmin
      },
      { skip: !isRangeEmpty || !isNotAllTimeRange }
    )
  const hasAnyHistorical = (anyHistoricalLeads?.length ?? 0) > 0
  const isCheckingHistorical =
    isRangeEmpty && isNotAllTimeRange && isFetchingAllTime
  const shouldHideHeader = !isFetching && (leadLogs?.length ?? 0) === 0

  return (
    <>
      <Table
        isLoading={isFetching}
        data={leadLogs ?? []}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="leads"
        searchPlaceholder="Search website leads…"
        extraHeaderElement={
          <DateFilter
            ref={dateFilterRef}
            selectedRange={dateFilter.range}
            customStartDate={dateFilter.customStartDate}
            customEndDate={dateFilter.customEndDate}
            onRangeChange={handleDateRangeChange}
            onClearFilter={handleClearDateFilter}
          />
        }
        extraHeaderElementClassName="ml-auto"
        shrinkFilterSort
        defaultSortBy="timestamp"
        defaultSortDir="desc"
        hasColumnFilter={false}
        noHeader={shouldHideHeader}
        noDataElement={
          isCheckingHistorical ? (
            <td colSpan={columns.length} className="p-0">
              <div className="min-h-[400px]" />
            </td>
          ) : isRangeEmpty && hasAnyHistorical && !isFetchingAllTime ? (
            <td colSpan={columns.length} className="p-0">
              <NoResultsInRangeMessage
                type="leads"
                onViewLast12Months={handleViewLast12Months}
                onSelectDateRange={() => dateFilterRef.current?.openAndFocus()}
              />
            </td>
          ) : undefined
        }
      />
      <PushToEngagements
        brandId={selectedLeadLog?.group_id}
        leadId={selectedLeadLog?.id}
        onCancel={cancelCreateEngagement}
      />
    </>
  )
}

export default LeadsTable
